import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Star, Shield, Clock, Check, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roofTypes = [
  'Colorbond',
  'Terracotta Tile',
  'Concrete Tile',
  'Metal/Corrugated',
  'Slate',
  'Other'
];

const suburbs = [
  'Katoomba',
  'Leura',
  'Springwood',
  'Blackheath',
  'Wentworth Falls',
  'Hazelbrook',
  'Blaxland',
  'Glenbrook',
  'Lawson',
  'Faulconbridge',
  'Other'
];

export default function HeroSection({ isLoaded }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    suburb: '',
    roofType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const heroRef = useRef(null);
  const isInView = useInView(heroRef, { once: true });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        address: formData.address || null,
        suburb: formData.suburb || null,
        roof_type: formData.roofType || null,
        message: formData.message || null
      };
      
      await axios.post(`${API}/quote`, payload);
      
      toast.success('Quote request submitted!', {
        description: 'We\'ll get back to you within 24 hours.'
      });
      
      setFormData({
        name: '',
        phone: '',
        email: '',
        address: '',
        suburb: '',
        roofType: '',
        message: ''
      });
    } catch (error) {
      console.error('Quote submission error:', error);
      toast.error('Failed to submit quote', {
        description: 'Please try again or call us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const headlineWords = ['Roof', 'Painting', '&', 'Restoration'];
  const subHeadline = ['in', 'the', 'Blue', 'Mountains'];

  return (
    <section 
      id="quote"
      ref={heroRef}
      className="relative min-h-screen pt-20 md:pt-24 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B111E] via-[#0B111E]/95 to-[#0B111E]/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B111E] via-transparent to-transparent" />
      </div>

      {/* Animated Grid Pattern */}
      <div className="absolute inset-0 z-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(56, 189, 248, 0.3) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(56, 189, 248, 0.3) 1px, transparent 1px)`,
          backgroundSize: '80px 80px'
        }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Google Rating Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-3 glass px-4 py-2 rounded-full"
            >
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/5/53/Google_%22G%22_Logo.svg" 
                alt="Google" 
                className="w-5 h-5"
              />
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#FBBF24] text-[#FBBF24]" />
                ))}
              </div>
              <span className="text-sm font-medium text-white">5.0 · 114 Reviews</span>
            </motion.div>

            {/* Main Headline */}
            <div className="space-y-2">
              <div className="overflow-hidden">
                <motion.div
                  initial={{ y: 100 }}
                  animate={isLoaded ? { y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="flex flex-wrap gap-x-4"
                >
                  {headlineWords.map((word, i) => (
                    <motion.span
                      key={i}
                      initial={{ opacity: 0, y: 50 }}
                      animate={isLoaded ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.6, delay: 0.4 + i * 0.1 }}
                      className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white"
                    >
                      {word}
                    </motion.span>
                  ))}
                </motion.div>
              </div>
              
              <div className="overflow-hidden">
                <motion.div
                  initial={{ y: 100 }}
                  animate={isLoaded ? { y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="flex flex-wrap gap-x-3"
                >
                  {subHeadline.map((word, i) => (
                    <motion.span
                      key={i}
                      initial={{ opacity: 0 }}
                      animate={isLoaded ? { opacity: 1 } : {}}
                      transition={{ duration: 0.5, delay: 0.7 + i * 0.1 }}
                      className={`text-3xl sm:text-4xl lg:text-5xl font-bold ${
                        word === 'Blue' || word === 'Mountains' 
                          ? 'text-[#38BDF8]' 
                          : 'text-[#94A3B8]'
                      }`}
                    >
                      {word}
                    </motion.span>
                  ))}
                </motion.div>
              </div>
            </div>

            {/* Value Props */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="grid grid-cols-2 gap-4"
            >
              {[
                { icon: Shield, text: '15 Year Warranty' },
                { icon: Clock, text: 'Fast Turnaround' },
                { icon: Check, text: 'Licensed & Insured' },
                { icon: Star, text: '5-Star Rated' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  whileHover={{ scale: 1.02, x: 5 }}
                  className="flex items-center gap-3 text-[#94A3B8]"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#38BDF8]/10 flex items-center justify-center">
                    <item.icon className="w-4 h-4 text-[#38BDF8]" />
                  </div>
                  <span className="text-sm font-medium">{item.text}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 1.1 }}
              className="flex gap-8 pt-4"
            >
              {[
                { value: '1,500+', label: 'Roofs Painted' },
                { value: '25+', label: 'Years Experience' },
                { value: '100%', label: 'Satisfaction' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-white">{stat.value}</div>
                  <div className="text-xs text-[#94A3B8] uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right - Quote Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isLoaded ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#38BDF8]/20 to-[#F95738]/20 rounded-2xl blur-xl" />
            <div 
              className="relative glass rounded-2xl p-6 md:p-8 glow-border"
              data-testid="get-quote-form"
            >
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">Get Your Fast Quote</h3>
                <p className="text-[#94A3B8] text-sm">Free inspection & no obligation quote</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    placeholder="Your Name *"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="bg-[#0B111E]/50 border-white/10 text-white placeholder:text-[#94A3B8] focus:border-[#38BDF8] h-12"
                    data-testid="name-input"
                  />
                  <Input
                    placeholder="Phone Number *"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="bg-[#0B111E]/50 border-white/10 text-white placeholder:text-[#94A3B8] focus:border-[#38BDF8] h-12"
                    data-testid="phone-input"
                  />
                </div>

                <Input
                  placeholder="Email Address *"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-[#0B111E]/50 border-white/10 text-white placeholder:text-[#94A3B8] focus:border-[#38BDF8] h-12"
                  data-testid="email-input"
                />

                <Input
                  placeholder="Street Address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="bg-[#0B111E]/50 border-white/10 text-white placeholder:text-[#94A3B8] focus:border-[#38BDF8] h-12"
                  data-testid="address-input"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Select 
                    value={formData.suburb} 
                    onValueChange={(value) => setFormData({ ...formData, suburb: value })}
                  >
                    <SelectTrigger 
                      className="bg-[#0B111E]/50 border-white/10 text-white h-12"
                      data-testid="suburb-input"
                    >
                      <SelectValue placeholder="Select Suburb" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111A2E] border-white/10">
                      {suburbs.map((suburb) => (
                        <SelectItem key={suburb} value={suburb} className="text-white hover:bg-[#38BDF8]/20">
                          {suburb}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select 
                    value={formData.roofType} 
                    onValueChange={(value) => setFormData({ ...formData, roofType: value })}
                  >
                    <SelectTrigger 
                      className="bg-[#0B111E]/50 border-white/10 text-white h-12"
                      data-testid="roof-type-select"
                    >
                      <SelectValue placeholder="Roof Type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111A2E] border-white/10">
                      {roofTypes.map((type) => (
                        <SelectItem key={type} value={type} className="text-white hover:bg-[#38BDF8]/20">
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Textarea
                  placeholder="Tell us about your project..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="bg-[#0B111E]/50 border-white/10 text-white placeholder:text-[#94A3B8] focus:border-[#38BDF8] min-h-[100px] resize-none"
                  data-testid="message-input"
                />

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-14 cta-button text-white font-semibold text-lg rounded-xl border-0"
                  data-testid="quote-submit-button"
                >
                  {isSubmitting ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full"
                    />
                  ) : (
                    'Get My Free Quote'
                  )}
                </Button>

                <p className="text-center text-xs text-[#94A3B8]">
                  By submitting, you agree to our Privacy Policy. We'll never spam you.
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center gap-2 text-[#94A3B8]"
        >
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <ChevronDown className="w-5 h-5" />
        </motion.div>
      </motion.div>
    </section>
  );
}
